import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetWhyChooseUsItems } from "../../hooks/why-choose-us/useGetWhyChooseUsItems";
import { useDeleteWhyChooseUsItem } from "../../hooks/why-choose-us/useDeleteWhyChooseUsItem";
import { useDeleteAllWhyChooseUsItems } from "../../hooks/why-choose-us/useDeleteAllWhyChooseUsItems";
import { useRestoreAllWhyChooseUsItems } from "../../hooks/why-choose-us/useRestoreAllWhyChooseUsItems";
import { useForceDeleteAllWhyChooseUsItems } from "../../hooks/why-choose-us/useForceDeleteAllWhyChooseUsItems";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import WhyChooseUsTableRow from "./Table/WhyChooseUsTableRow";
import { WhyChooseUsTableColumns } from "./Table/WhyChooseUsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const WhyChooseUsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("why-choose-us.view"),
            create: has("why-choose-us.create"),
            edit: has("why-choose-us.update"),
            delete: has("why-choose-us.delete"),
        }),
        [has]
    );

    const navigate = useNavigate();

    const [trashedFilter, setTrashedFilter] = useState("");

    const { deleteItemMutate, isPending: isDelete } = useDeleteWhyChooseUsItem();
    const { deleteAllItemsMutate, isPending: isDeleteAll } = useDeleteAllWhyChooseUsItems();
    const { restoreAllItemsMutate, isPending: isRestoreAll } = useRestoreAllWhyChooseUsItems();
    const { forceDeleteAllItemsMutate, isPending: isForceDeleteAll } = useForceDeleteAllWhyChooseUsItems();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/why-choose-us",
        deleteSingle: deleteItemMutate,
        deleteAll: deleteAllItemsMutate,
        restoreSingle: (id, options) => restoreAllItemsMutate({ items: [id] }, options),
        restoreAll: restoreAllItemsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllItemsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllItemsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            sort_by: 'sort_order',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, order]
    );

    const { isLoading, error, items, meta } = useGetWhyChooseUsItems(params);

    const columns = useMemo(() => WhyChooseUsTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "لماذا نحن", en: "Why Choose Us" }}
            columns={columns}
            items={items}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={[]}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, items)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/why-choose-us/create")}
            addButtonLabel={{ ar: "إضافة عنصر", en: "Add Item" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <WhyChooseUsTableRow
                    {...params}
                    key={params.item.id}
                    item={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default WhyChooseUsTable;
